import React, { PropsWithChildren, useEffect } from 'react';

import { Router } from 'next/router';
import * as Sentry from '@sentry/nextjs';

import { routes } from '@routes';

import { config } from '@utils/config';
import { link } from '@utils/link';
import { getGaClientId } from '@utils/google-analytics';

export type DataLayer = {
  push: (params: { [key: string]: any }) => void;
};

const GTM_CODE = 'GTM-5K277SF';

const isValidPath = (path?: string): path is string => {
  return Boolean(path);
};

const ignoredRouteChanges: string[] = [
  routes.estimateSurvey,
  routes.brokerHandover,
  routes.surveyToSell,
  routes.estimateSurveyV3,
  routes.buyersSurvey,
  routes.atlasAdCatalog,
  routes.buyersSurvey,
  routes.mortgageSurvey,
]
  .map((route) => link(route).as)
  .filter<string>(isValidPath);

/**
 *  Adds pageview into dataLayer
 *  @param {string} virtualUrl The url to be pushed into dataLayer
 *  @param {Object} data Extra data to be pushed along with url
 *  @returns {Boolean} true if successful, false if dataLayer isn't ready
 */
export const dataLayerPushVirtualUrl = (
  virtualUrl: string,
  data?: Record<string, any>,
): boolean => {
  if (!config.GTM_ENABLED) {
    console.debug('Pushing into datalayer', {
      ...data,
      event: 'virtualPV',
      virtualUrl,
    });

    return true;
  }

  if (!window.dataLayer) return false;

  window.dataLayer.push({ ...data, event: 'virtualPV', virtualUrl });

  return true;
};

/**
 * Custom `gtag` function, to make sure we don't rely on google analytics gtag implementation
 * Source: https://support.google.com/optimize/answer/9059383?hl=en
 */
export const gtag = function (..._args: any[]) {
  if (!window.dataLayer) return false;
  // eslint-disable-next-line
  window.dataLayer.push(arguments);

  return true;
};

export const GTMScript = () =>
  config.GTM_ENABLED ? (
    <script
      src={`https://www.googletagmanager.com/gtm.js?id=${GTM_CODE}`}
      defer
    />
  ) : null;

type Props = PropsWithChildren<{}>;

export const GTMProvider = ({ children }: Props) => {
  useEffect(() => {
    window.dataLayer = window.dataLayer || [];

    window.dataLayer.push({
      originalLocation:
        document.location.protocol +
        '//' +
        document.location.hostname +
        document.location.pathname +
        document.location.search,
    });

    window.dataLayer.push({
      'gtm.start': new Date().getTime(),
      event: 'gtm.js',
    });

    // on route change ga event
    Router.events.on('routeChangeComplete', () => {
      const { pathname, search } = window.location;

      // Do not track on ignored routes (surveys, broker handover)
      if (ignoredRouteChanges.some((path) => pathname.startsWith(path))) return;

      const newUrl = `${pathname}${search}`;
      dataLayerPushVirtualUrl(newUrl);
    });

    // Store clientId to Sentry for easier tracking.
    const timeoutId = setTimeout(() => {
      const clientId = getGaClientId();

      if (clientId.startsWith('NO_GA')) {
        Sentry.setUser({
          hasGa: false,
        });
        Sentry.setTag('clientId', null);
      } else {
        Sentry.setUser({
          hasGa: true,
          clientId,
        });
        Sentry.setTag('clientId', clientId);
      }
    }, 5000);

    return () => {
      clearTimeout(timeoutId);
    };
  }, []);

  return <>{children}</>;
};
